/**
 * Scaffolding Cost Calculator — public calculator page (PRP-CALC-1 S3).
 *
 * Estimates scaffold square footage, rental duration, and cost for a
 * restoration facade from published public rate schedules, and produces a
 * justification narrative for the estimate file. Carries the /tools SEO
 * treatment: per-route meta + JSON-LD, coverage section, app promo band.
 */
import { useState } from 'react';

import { Button } from '../../components/ui/Button';
import ToolPageShell, { DimensionDivider, Eyebrow } from './ToolPageShell';
import AppPromo from './AppPromo';
import CalcResults from './CalcResults';
import ScenarioInterview from './ScenarioInterview';
import {
  MethodSection,
  FaqSection,
  SourcesSection,
  AdvancedRegional,
  NumberField,
  SelectField,
  CheckboxField,
  type FaqItem,
} from './_shared';
import { useSeo, faqJsonLd, calculatorJsonLd } from './useSeo';
import { CalcAPI, type CalcResponse, type ScaffoldConfig } from './calcApi';

const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'How much does scaffolding cost to rent?',
    a: 'Scaffold cost is two separate lines, not one number. First is erect and dismantle labor, priced from the square feet of elevation your crew has to build and take back down. Second is rental time, billed while the scaffold stands. Rental yards commonly bill in 28-day cycles rather than by the exact day, so a job that spills a few days past a cycle can pick up another month of rent. The rate per square foot per month varies enough by region, height, and system type that a real quote should come from a local yard. The calculator prices your specific facade from published rate schedules and shows the labor line and the rental line separately so you can see what drives the total.',
  },
  {
    q: 'How is scaffold square footage figured?',
    a: 'The run of each wall is added together and multiplied by the height. Two walls of 40 and 24 feet at 20 feet tall is 64 times 20, or 1,280 square feet of scaffold. That elevation area drives both the erect and dismantle labor and the monthly rental.',
  },
  {
    q: 'Why is scaffold billed by the month?',
    a: 'The labor to erect and dismantle is a one-time cost. Everything in between is rental, and most yards bill rental in monthly or 28-day cycles rather than by the day. That is why the weeks or months on the wall matter as much as the size: a facade that stays up through a slow repair keeps accruing rent even when no one is working on it.',
  },
  {
    q: 'What makes a job run longer than planned?',
    a: 'Weather is the usual one. Rain, wind, and freeze days stop exterior work but do not stop the rental clock, so a repair that should take three weeks can push into a second billing cycle. Change orders, waiting on materials, and inspections do the same. When you estimate the duration, use the time the scaffold is standing, not the time crews are actively working on it.',
  },
  {
    q: 'When does scaffold need to be engineered?',
    a: 'As a common rule of thumb, scaffold over about 125 feet tall requires an engineered design by a qualified engineer, because the standard tie and base assumptions no longer hold. The tool returns an engineering recommendation instead of a line-item price when your height crosses that range, since pricing an un-engineered tower that tall would be misleading.',
  },
  {
    q: 'Frame or system scaffold, which should I choose?',
    a: 'Frame scaffold is economical for straight, uniform walls and is cheaper to rent, but it is slower to build on irregular elevations. System scaffold costs more per square foot and goes up faster, and it handles irregular facades and heavy loading better. The system type you pick changes both the labor line and the rental line, so the tool asks for it.',
  },
  {
    q: 'Does the price include tear-down?',
    a: 'Yes. The erect and dismantle line item covers both building the scaffold and taking it back down. The rental line covers the time in between. Debris netting and extra working levels are added as their own line items so the estimate shows what each choice costs.',
  },
  {
    q: 'Can I use this number in an Xactimate supplement?',
    a: 'Yes. The result gives you the scaffold area, the erect and dismantle basis, the rental duration, and a narrative you can paste into the supplement. It is a planning estimate built from published public rate schedules, so confirm the final price with a local scaffold yard before you rely on it.',
  },
];

export default function ScaffoldCalculatorPage() {
  useSeo({
    title:
      'Scaffolding Cost Calculator — Rental Cost per SF & Month | Antero Trail',
    description:
      'Free scaffolding cost calculator for restoration and insurance claims. Enter your facade runs and height to size scaffold square footage, rental duration, and cost from published rate schedules, with a narrative for your supplement.',
    path: '/tools/scaffolding-cost-calculator',
    jsonLd: [
      calculatorJsonLd({
        name: 'Scaffolding Cost Calculator for Restoration Estimates',
        description:
          'Sizes scaffold square footage from the facade runs and height, estimates the erect, dismantle, and rental cost from published public rate schedules, and writes a justification narrative for restoration supplements.',
        path: '/tools/scaffolding-cost-calculator',
      }),
      faqJsonLd(FAQ_ITEMS),
    ],
  });

  const [mode, setMode] = useState<'scenario' | 'advanced'>('scenario');
  const [facades, setFacades] = useState('40, 24');
  const [height, setHeight] = useState<number>(20);
  const [levels, setLevels] = useState<number>(2);
  const [weeks, setWeeks] = useState<number>(4);
  const [config, setConfig] = useState<ScaffoldConfig>('frame');
  const [netting, setNetting] = useState(false);
  const [regional, setRegional] = useState(0);

  const [result, setResult] = useState<CalcResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function parseFacades(): number[] {
    return facades
      .split(/[,\s]+/)
      .map((s) => parseFloat(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const lengths = parseFacades();
      if (lengths.length === 0) {
        setError('Enter at least one facade length, for example 40, 24.');
        setLoading(false);
        return;
      }
      const res = await CalcAPI.scaffold(
        {
          facade_lengths_ft: lengths,
          height_ft: height,
          working_levels: levels,
          duration_weeks: weeks,
          config,
          debris_netting: netting,
        },
        regional,
      );
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <ToolPageShell>
      <Eyebrow>Lifting &amp; access — free calculator</Eyebrow>
      <h1 className="mt-2 font-ui text-4xl font-bold tracking-tight sm:text-[2.75rem] sm:leading-tight">
        Scaffolding Cost Calculator for Restoration Estimates
      </h1>
      <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
        Size scaffold square footage, rental duration, and cost for a
        restoration facade from published public rate schedules. Enter the run
        of each wall you need to reach, the height, and how long the scaffold
        stays up, and the tool splits the cost into erect-and-dismantle labor
        and rental time so you can see what drives the number.
      </p>

      {/* Mode toggle: describe the job (S8 scenario) vs raw parameters */}
      <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Input mode">
        <Button
          type="button"
          role="tab"
          aria-selected={mode === 'scenario'}
          variant={mode === 'scenario' ? 'primary' : 'outline'}
          onClick={() => setMode('scenario')}
        >
          Describe the job
        </Button>
        <Button
          type="button"
          role="tab"
          aria-selected={mode === 'advanced'}
          variant={mode === 'advanced' ? 'primary' : 'outline'}
          onClick={() => setMode('advanced')}
        >
          Advanced mode (engineering inputs)
        </Button>
      </div>

      {mode === 'scenario' && (
        <div className="mt-6 max-w-3xl rounded-lg border border-border bg-card p-5 sm:p-6">
          <ScenarioInterview calculator="scaffold" />
        </div>
      )}

      <div
        className={mode === 'advanced' ? 'mt-8 grid gap-8 lg:grid-cols-2' : 'hidden'}
      >
        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-lg border border-border bg-card p-5 sm:p-6"
          aria-label="Scaffold inputs"
        >
          <div className="space-y-1.5">
            <label
              htmlFor="facades"
              className="text-sm font-medium leading-none text-foreground"
            >
              Facade lengths{' '}
              <span className="font-normal text-muted-foreground">(ft)</span>
            </label>
            <input
              id="facades"
              type="text"
              value={facades}
              onChange={(e) => setFacades(e.target.value)}
              placeholder="40, 24"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
            <p className="text-xs text-muted-foreground">
              One number per wall, separated by commas. The tool adds them up.
            </p>
          </div>
          <NumberField
            label="Height"
            suffix="ft"
            value={height}
            min={1}
            onChange={setHeight}
            hint="Total height the scaffold must reach from grade."
          />
          <NumberField
            label="Working levels"
            value={levels}
            min={1}
            step="1"
            onChange={setLevels}
            hint="Number of planked levels crews will work from."
          />
          <NumberField
            label="Duration on site"
            suffix="weeks"
            value={weeks}
            min={1}
            step="1"
            onChange={setWeeks}
          />
          <SelectField<ScaffoldConfig>
            label="System type"
            value={config}
            onChange={setConfig}
            options={[
              { value: 'frame', label: 'Frame scaffold' },
              { value: 'system', label: 'System scaffold' },
            ]}
          />
          <div className="pt-1">
            <CheckboxField
              label="Debris netting"
              checked={netting}
              onChange={setNetting}
            />
          </div>

          <AdvancedRegional value={regional} onChange={setRegional} />

          <Button type="submit" isLoading={loading} className="w-full">
            Estimate scaffold cost
          </Button>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </form>

        <div>
          {result ? (
            <CalcResults result={result} />
          ) : (
            <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Enter your facade and select Estimate scaffold cost to see square
              footage, a cost range, and a justification narrative.
            </div>
          )}
        </div>
      </div>

      <section className="mt-14">
        <DimensionDivider label="What it covers" />
        <h2 className="mb-3 mt-5 font-ui text-2xl font-bold tracking-tight">
          Facade scaffold, sized from the wall
        </h2>
        <p className="mb-5 max-w-3xl text-[15px] leading-relaxed text-muted-foreground">
          The tool covers exterior facade scaffold, the kind you stand up to
          reach a wall for siding, stucco, paint, masonry, or window work. It
          works the way an estimator does:
        </p>
        <ul className="grid max-w-4xl gap-x-8 gap-y-3 sm:grid-cols-1">
          <li className="flex gap-3">
            <span aria-hidden className="mt-2 h-2.5 w-px shrink-0 bg-amber-500" />
            <span className="text-sm leading-relaxed">
              <span className="font-semibold text-foreground">
                Size from linear feet times height.
              </span>{' '}
              <span className="text-muted-foreground">
                Add up the run of every wall you need to reach, multiply by the
                height from grade, and you have the square feet of elevation the
                scaffold covers. That area sets both the erect-and-dismantle
                labor and the rental.
              </span>
            </span>
          </li>
          <li className="flex gap-3">
            <span aria-hidden className="mt-2 h-2.5 w-px shrink-0 bg-amber-500" />
            <span className="text-sm leading-relaxed">
              <span className="font-semibold text-foreground">
                Months on the wall, not days of work.
              </span>{' '}
              <span className="text-muted-foreground">
                Rental is billed for the whole time the scaffold stands,
                commonly in 28-day cycles. Weather and change orders stretch
                that duration, so the estimate uses the time the tower is up,
                not the hours crews spend on it.
              </span>
            </span>
          </li>
          <li className="flex gap-3">
            <span aria-hidden className="mt-2 h-2.5 w-px shrink-0 bg-amber-500" />
            <span className="text-sm leading-relaxed">
              <span className="font-semibold text-foreground">
                Interior shoring is coming.
              </span>{' '}
              <span className="text-muted-foreground">
                This tool prices exterior facade scaffold today. Interior
                shoring and shoring towers for ceilings and elevated slabs are
                a separate branch we are still building, so use the facade path
                for now.
              </span>
            </span>
          </li>
        </ul>
      </section>

      <AppPromo
        className="mt-14"
        context="In the app, the scaffold package lands in your estimate as Xactimate SCF items with the sizing narrative attached, so the square footage, duration, and rate basis carry straight into the file."
      />

      <MethodSection>
        <p>
          Scaffold is priced by the square foot of elevation, which is the wall
          run multiplied by the height, plus the time it stays standing. The
          tool adds up each facade you enter, multiplies by the height to get
          the scaffold area, and then applies an erect and dismantle rate plus a
          rental rate from published equipment schedules. Frame scaffold is
          cheaper to rent but slower to build on irregular walls, while system
          scaffold costs more per square foot and goes up faster, so the system
          type you pick changes both the labor and the rental line.
        </p>
        <p>
          Height drives more than area. Above roughly 125 feet, scaffold usually
          needs an engineered drawing and a design by a qualified engineer,
          because the standard tie and base assumptions no longer hold. When the
          height crosses that line the tool stops giving a line-item price and
          returns a recommendation to have the scaffold engineered instead,
          because pricing an un-engineered tower at that height would be
          misleading. Debris netting and extra working levels are added as their
          own line items so the estimate shows what each choice costs.
        </p>
      </MethodSection>

      <FaqSection items={FAQ_ITEMS} />

      <SourcesSection
        sources={[
          {
            name: 'Caltrans Labor Surcharge and Equipment Rental Rates',
            detail:
              'Published equipment rental rate schedule used as the public basis for scaffold rental and labor rates.',
          },
          {
            name: 'USACE equipment rate references',
            detail:
              'Federal equipment cost references used to cross-check the rental basis.',
          },
          {
            name: 'OSHA 1926 Subpart L, Scaffolds',
            detail:
              'Design, capacity, and the requirement that scaffolds beyond standard assumptions be designed by a qualified engineer.',
          },
        ]}
      />
    </ToolPageShell>
  );
}
